import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { SettingsLayout } from '@/components/layout/SettingsLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { RestrictedRoute } from './RestrictedRoute';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { AdminRoute } from './AdminRoute';
import { MdmFeatureRoute } from './MdmFeatureRoute';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { DesignerPermissionGate } from '@/features/designer/components/DesignerPermissionGate';
import { RedirectToSignup } from '@/features/referral/RedirectToSignup';
import { lazyWithRetry } from '@/lib/lazy-with-retry';

// Auth pages (lazy loaded — retries once on chunk load failure after deployments)
const LoginPage = lazyWithRetry(() => import('@/features/auth/pages/LoginPage'));
const OptiDevSignInPage = lazyWithRetry(() => import('@/features/auth/pages/OptiDevSignInPage'));
const SignupPage = lazyWithRetry(() => import('@/features/auth/pages/SignupPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/features/auth/pages/ForgotPasswordPage'));
const MfaPage = lazyWithRetry(() => import('@/features/auth/pages/MfaPage'));
const SamlLoginPage = lazyWithRetry(() => import('@/features/auth/pages/SamlLoginPage'));
const SamlAppLoginPage = lazyWithRetry(() => import('@/features/auth/pages/SamlAppLoginPage'));
const SsoLoginPage = lazyWithRetry(() => import('@/features/auth/pages/SsoLoginPage'));
const PasswordResetPage = lazyWithRetry(() => import('@/features/auth/pages/PasswordResetPage'));
const VerifyEmailPage = lazyWithRetry(() => import('@/features/auth/pages/VerifyEmailPage'));
const MagicLinkSignInPage = lazyWithRetry(
  () => import('@/features/auth/pages/MagicLinkSignInPage'),
);
const JoinTeamPage = lazyWithRetry(() => import('@/features/auth/pages/JoinTeamPage'));

// Feature pages (lazy loaded, behind feature flags)
const ScreensPage = lazyWithRetry(() => import('@/features/screens/pages/ScreensPage'));
const useNativeScreens = import.meta.env.VITE_USE_NATIVE_SCREENS === 'true';

const AssetsPage = lazyWithRetry(() => import('@/features/assets/pages/AssetsPage'));
const useNativeAssets = import.meta.env.VITE_USE_NATIVE_ASSETS === 'true';

// Hidden route — not surfaced in nav. Reached via a Cloudflare redirect from
// emergency.optisigns.com/app/emergencyMessage. No feature flag / Scio1Frame fallback:
// this kebab-case path is scio3-only (scio1 serves `emergencyMessage`, not this route),
// so there is nothing to fall back to — always render native.
const EmergencyControlCenterPage = lazyWithRetry(
  () => import('@/features/emergency/pages/EmergencyControlCenterPage'),
);

const PlaylistsPage = lazyWithRetry(() => import('@/features/playlists/pages/PlaylistsPage'));
const useNativePlaylists = import.meta.env.VITE_USE_NATIVE_PLAYLISTS === 'true';

const SchedulesPage = lazyWithRetry(() => import('@/features/schedules/pages/SchedulesPage'));
const useNativeSchedules = import.meta.env.VITE_USE_NATIVE_SCHEDULES === 'true';

const PersonalProfilePage = lazyWithRetry(
  () => import('@/features/settings/pages/PersonalProfilePage'),
);
const useNativeProfile = import.meta.env.VITE_USE_NATIVE_PROFILE === 'true';

const CompanyProfilePage = lazyWithRetry(
  () => import('@/features/settings/pages/CompanyProfilePage'),
);
const useNativeCompanyProfile = import.meta.env.VITE_USE_NATIVE_COMPANY_PROFILE === 'true';

const ExternalComsPage = lazyWithRetry(
  () => import('@/features/external-coms/pages/ExternalComsPage'),
);
const useNativeExternalComs = import.meta.env.VITE_USE_NATIVE_EXTERNAL_COMS === 'true';

const PreferencesPage = lazyWithRetry(() => import('@/features/settings/pages/PreferencesPage'));
const useNativePreferences = import.meta.env.VITE_USE_NATIVE_PREFERENCES === 'true';

const ApiKeysPage = lazyWithRetry(() => import('@/features/settings/pages/ApiKeysPage'));
const useNativeApiKeys = import.meta.env.VITE_USE_NATIVE_APIKEYS === 'true';

const AccountMembersPage = lazyWithRetry(
  () => import('@/features/account-members/pages/AccountMembersPage'),
);
const useNativeAccountMembers = import.meta.env.VITE_USE_NATIVE_ACCOUNT_MEMBERS === 'true';

const SamlSettingsPage = lazyWithRetry(() => import('@/features/settings/pages/SamlSettingsPage'));
const useNativeSamlSettings = import.meta.env.VITE_USE_NATIVE_SAML_SETTINGS === 'true';

const OperationalSchedulePage = lazyWithRetry(
  () => import('@/features/settings/pages/OperationalSchedulePage'),
);
const useNativeOpsSchedule = import.meta.env.VITE_USE_NATIVE_OPS_SCHEDULE === 'true';

const MonitoringAlertRulesPage = lazyWithRetry(
  () => import('@/features/monitoring-alert-rules/pages/MonitoringAlertRulesPage'),
);
const useNativeMonitoringAlertRules =
  import.meta.env.VITE_USE_NATIVE_MONITORING_ALERT_RULES === 'true';

const ProofOfPlayPage = lazyWithRetry(() => import('@/features/analytics/pages/ProofOfPlayPage'));

const ProofOfPlaySettingsPage = lazyWithRetry(
  () => import('@/features/proof-of-play-settings/pages/ProofOfPlaySettingsPage'),
);
const useNativePopSettings = import.meta.env.VITE_USE_NATIVE_POP_SETTINGS === 'true';
const AiAnalyticsPage = lazyWithRetry(() => import('@/features/analytics/pages/AiAnalyticsPage'));

const EngagePage = lazyWithRetry(() => import('@/features/engage/pages/EngagePage'));
const EngageHubPage = lazyWithRetry(() => import('@/features/engage/pages/EngageHubPage'));
const useNativeEngage = import.meta.env.VITE_USE_NATIVE_ENGAGE === 'true';

const FeedsPageLazy = lazyWithRetry(() =>
  import('@/features/feeds/pages/FeedsPage').then((m) => ({
    default: () => <m.default mode="app" />,
  })),
);
const FeedsPagePublicLazy = lazyWithRetry(() =>
  import('@/features/feeds/pages/FeedsPage').then((m) => ({
    default: () => <m.default mode="public" />,
  })),
);
const useNativeFeeds = import.meta.env.VITE_USE_NATIVE_FEEDS === 'true';
const useNativeFeedsAdmin = import.meta.env.VITE_USE_NATIVE_FEEDS_ADMIN === 'true';
const usePublicFeedsSite = import.meta.env.VITE_USE_PUBLIC_FEEDS_SITE === 'true';

const FeedsAdminPageLazy = lazyWithRetry(() => import('@/features/feeds/pages/FeedsAdminPage'));

const ProvisioningTemplatesPage = lazyWithRetry(
  () => import('@/features/provisioning-templates/pages/ProvisioningTemplatesPage'),
);
const WifiProfilesPage = lazyWithRetry(
  () => import('@/features/provisioning-templates/pages/WifiProfilesPage'),
);

const TemplatesPage = lazyWithRetry(() => import('@/features/templates/pages/TemplatesPage'));
const useNativeTemplates = import.meta.env.VITE_USE_NATIVE_TEMPLATES === 'true';

const DesignerPage = lazyWithRetry(() => import('@/features/designer/pages/DesignerPage'));
const useNativeDesigner = import.meta.env.VITE_USE_NATIVE_DESIGNER === 'true';
const DesignEditorPage = lazyWithRetry(() => import('@/features/designer/pages/DesignEditorPage'));
const AuditLogPage = lazyWithRetry(() => import('@/features/audit-logs/pages/AuditLogPage'));
const useNativeAuditLog = import.meta.env.VITE_USE_NATIVE_AUDIT_LOG === 'true';

const DataSourcesPage = lazyWithRetry(() => import('@/features/datasources/pages/DataSourcesPage'));
const useNativeDataSources = import.meta.env.VITE_USE_NATIVE_DATASOURCES === 'true';

const IntegrationsPage = lazyWithRetry(
  () => import('@/features/integrations/pages/IntegrationsPage'),
);
const useNativeIntegrations = import.meta.env.VITE_USE_NATIVE_INTEGRATIONS === 'true';

// Billing pages (lazy loaded, behind feature flag)
const SubscriptionPage = lazyWithRetry(() => import('@/features/billing/pages/SubscriptionPage'));
const PaymentMethodPage = lazyWithRetry(() => import('@/features/billing/pages/PaymentMethodPage'));
const PaymentHistoryPage = lazyWithRetry(
  () => import('@/features/billing/pages/PaymentHistoryPage'),
);
const SubAccountBillingPage = lazyWithRetry(
  () => import('@/features/billing/pages/SubAccountBillingPage'),
);
const CouponsPage = lazyWithRetry(() => import('@/features/billing/pages/CouponsPage'));
const TeamBillingPage = lazyWithRetry(() => import('@/features/billing/pages/TeamBillingPage'));
const PaymentIntentPage = lazyWithRetry(() => import('@/features/billing/pages/PaymentIntentPage'));
const ConfirmLinkedAccountPage = lazyWithRetry(
  () => import('@/features/billing/pages/ConfirmLinkedAccountPage'),
);
const OrderDevicePage = lazyWithRetry(() => import('@/features/billing/pages/OrderDevicePage'));
const OrderHistoryPage = lazyWithRetry(() => import('@/features/billing/pages/OrderHistoryPage'));
const useNativeBilling = import.meta.env.VITE_USE_NATIVE_BILLING === 'true';
const useNativeSubAccountBilling = import.meta.env.VITE_USE_NATIVE_SUB_ACCOUNT_BILLING === 'true';
// Team Billing is independently gated alongside the per-team buttons on
// Account Members. When false, /app/s/team-billing falls through to scio1
// iframe (state app.account.teamBillingScio3) even with VITE_USE_NATIVE_BILLING=true.
const useNativeTeams = import.meta.env.VITE_USE_NATIVE_TEAMS === 'true';

const ShareLinksAdminPage = lazyWithRetry(
  () => import('@/features/share-links/pages/ShareLinksAdminPage'),
);
const useNativeShareLinksAdmin = import.meta.env.VITE_USE_NATIVE_SHARE_LINKS_ADMIN === 'true';

// Share link pages (standalone, no AppShell — accessible without login)
const SignInSharePage = lazyWithRetry(() => import('@/features/share-links/pages/SignInSharePage'));
const PlaylistSharePage = lazyWithRetry(
  () => import('@/features/share-links/pages/PlaylistSharePage'),
);
const FolderSharePage = lazyWithRetry(() => import('@/features/share-links/pages/FolderSharePage'));

const FunctionalLocationsPage = lazyWithRetry(
  () => import('@/features/settings/functional-locations/pages/FunctionalLocationsPage'),
);
const useNativeFunctionalLocations =
  import.meta.env.VITE_USE_NATIVE_FUNCTIONAL_LOCATIONS === 'true';

const BrandingSettingsPage = lazyWithRetry(
  () => import('@/features/branding-settings/pages/BrandingSettingsPage'),
);
const useNativeBrandingSettings = import.meta.env.VITE_USE_NATIVE_BRANDING_SETTINGS === 'true';

const OptiDevPage = lazyWithRetry(() => import('@/features/optidev/pages/OptiDevPage'));

const ReferralPage = lazyWithRetry(() => import('@/features/referral/pages/ReferralPage'));
const RevuePage = lazyWithRetry(() => import('@/features/revue/pages/RevuePage'));

// OptiEdge MDM (Phase 1) — Devices + Fleets pages
const DevicesPage = lazyWithRetry(() => import('@/features/mdm-devices/pages/DevicesPage'));
const FleetsPage = lazyWithRetry(() => import('@/features/mdm-fleets/pages/FleetsPage'));
const FleetDetailPage = lazyWithRetry(() => import('@/features/mdm-fleets/pages/FleetDetailPage'));

// OptiEdge MDM (Phase A) — Connect feature (Zoom Rooms + Teams/Webex/Meet stubs)
const ConnectLayout = lazyWithRetry(() => import('@/features/connect/pages/ConnectLayout'));
const ConnectHubPage = lazyWithRetry(() => import('@/features/connect/pages/ConnectHubPage'));
const ZoomRoomsPage = lazyWithRetry(() => import('@/features/connect/pages/ZoomRoomsPage'));
const TeamsRoomsPage = lazyWithRetry(() => import('@/features/connect/pages/TeamsRoomsPage'));
const WebexRoomsPage = lazyWithRetry(() => import('@/features/connect/pages/WebexRoomsPage'));
const GoogleMeetPage = lazyWithRetry(() => import('@/features/connect/pages/GoogleMeetPage'));
const ManifestPlaygroundPage = lazyWithRetry(
  () => import('@/features/smallapp/pages/ManifestPlaygroundPage'),
);

// Local-only dev routes (not committed — see .git/info/exclude)
const devRouteModules = import.meta.env.DEV
  ? import.meta.glob('./dev-routes.tsx', { eager: true })
  : {};
const devMod = devRouteModules['./dev-routes.tsx'] as
  | {
      devRootRoutes?: import('react-router').RouteObject[];
      devAppRoutes?: import('react-router').RouteObject[];
    }
  | undefined;

function LazyWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    children: [
      // === Public feeds site — only when VITE_USE_PUBLIC_FEEDS_SITE=true (separate deployment) ===
      // Root URL `/` renders full-screen feeds page without AppShell or auth.
      // "Next" redirects to the portal deployment (VITE_PORTAL_URL) for auth + asset creation.
      ...(usePublicFeedsSite
        ? [
            {
              index: true,
              element: (
                <LazyWrapper>
                  <FeedsPagePublicLazy />
                </LazyWrapper>
              ),
            },
          ]
        : []),

      ...(devMod?.devRootRoutes ?? []),

      // === Public Design Editor — standalone, no AppShell ===
      // Matches scio1 /designEditor and scio2 /design-editor.
      // Redirects authenticated users to /app/designManagement.
      {
        path: 'designEditor',
        element: (
          <LazyWrapper>
            <DesignEditorPage />
          </LazyWrapper>
        ),
      },
      // scio2 kebab-case alias
      {
        path: 'design-editor',
        element: (
          <LazyWrapper>
            <DesignEditorPage />
          </LazyWrapper>
        ),
      },

      // === Share Link pages — standalone, no AppShell, no auth required ===
      {
        path: 'share/signIn',
        element: (
          <LazyWrapper>
            <SignInSharePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'share/playlist/:objectId',
        element: (
          <LazyWrapper>
            <PlaylistSharePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'share/folder/:objectId',
        element: (
          <LazyWrapper>
            <FolderSharePage />
          </LazyWrapper>
        ),
      },

      // === Referral vanity link — root-level PUBLIC sibling, NO AppShell, NO auth ===
      // Must sit OUTSIDE/BEFORE the AppShell route so an unauthenticated referee
      // clicking `/r/<code>` drops the attribution cookie + redirects to /signUp
      // instead of being bounced to /signIn. Eager (not lazy) — it only sets a
      // cookie and redirects, so there's no payload worth code-splitting.
      {
        path: 'r/:code',
        element: <RedirectToSignup />,
      },

      // === Emergency Control Center — standalone, auth-protected, NO AppShell ===
      // Hidden route with its own minimal chrome (scio1 parity), kebab-case per scio3 convention.
      // Cloudflare redirects the legacy emergency.optisigns.com/app/emergencyMessage here. Registered
      // before the AppShell block so the static path outranks the AppShell `app/*` splat.
      {
        element: <ProtectedRoute />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'app/emergency-control-center',
            element: (
              <LazyWrapper>
                <EmergencyControlCenterPage />
              </LazyWrapper>
            ),
          },
        ],
      },

      // === AppShell layout — wraps all portal routes (auth, app, settings) ===
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          // Root redirect (only active when NOT public feeds site, since public feeds takes the index above)
          ...(!usePublicFeedsSite
            ? [{ index: true, element: <Navigate to="/app/screenManagement" replace /> }]
            : []),

          // === Auth routes (match scio1 exactly) ===
          // Sign in routes
          {
            path: 'signIn',
            element: (
              <LazyWrapper>
                <LoginPage />
              </LazyWrapper>
            ),
          },
          // OptiDev popup sign-in (scio1: /signIn/optidev?origin=...). Static segment
          // is ranked above signIn/:accountId, but keep it first for clarity.
          {
            path: 'signIn/optidev',
            element: (
              <LazyWrapper>
                <OptiDevSignInPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'signIn/:accountId',
            element: (
              <LazyWrapper>
                <LoginPage />
              </LazyWrapper>
            ),
          },
          // MFA (scio1: /signIn/mfa)
          {
            path: 'signIn/mfa',
            element: (
              <LazyWrapper>
                <MfaPage />
              </LazyWrapper>
            ),
          },
          // SAML (scio1: /signIn/saml)
          {
            path: 'signIn/saml',
            element: (
              <LazyWrapper>
                <SamlLoginPage />
              </LazyWrapper>
            ),
          },
          // SAML App — Flutter mobile app (scio1: /signIn/samlApp)
          {
            path: 'signIn/samlApp',
            element: (
              <LazyWrapper>
                <SamlAppLoginPage />
              </LazyWrapper>
            ),
          },

          // SAML callback from backend ACS redirect (open-api redirects to /sign-in/saml?token={tokenId})
          {
            path: 'sign-in/saml',
            element: (
              <LazyWrapper>
                <SamlLoginPage />
              </LazyWrapper>
            ),
          },
          // Legacy: keep /sign-in for backwards compatibility with older backend versions
          {
            path: 'sign-in',
            element: (
              <LazyWrapper>
                <SamlLoginPage />
              </LazyWrapper>
            ),
          },

          // Sign up routes (direct - match scio1 for marketing site compatibility)
          {
            path: 'signUp',
            element: (
              <LazyWrapper>
                <SignupPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'signUp/:planType',
            element: (
              <LazyWrapper>
                <SignupPage />
              </LazyWrapper>
            ),
          },

          // === Password routes (match scio1 exactly) ===
          // Forgot password (scio1: /password/forgot)
          {
            path: 'password/forgot',
            element: (
              <LazyWrapper>
                <ForgotPasswordPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'password/forgot/:accountId',
            element: (
              <LazyWrapper>
                <ForgotPasswordPage />
              </LazyWrapper>
            ),
          },
          // Password reset - Firebase oobCode format: /password/reset?oobCode=xxx
          {
            path: 'password/reset',
            element: (
              <LazyWrapper>
                <PasswordResetPage />
              </LazyWrapper>
            ),
          },
          // Email verification - Firebase oobCode format: /verify?oobCode=xxx&mode=verifyEmail
          {
            path: 'verify',
            element: (
              <LazyWrapper>
                <VerifyEmailPage />
              </LazyWrapper>
            ),
          },
          // Magic link sign-in
          {
            path: 'sign-email-link',
            element: (
              <LazyWrapper>
                <MagicLinkSignInPage />
              </LazyWrapper>
            ),
          },
          // Team invite acceptance
          {
            path: 'join',
            element: (
              <LazyWrapper>
                <JoinTeamPage />
              </LazyWrapper>
            ),
          },
          // SSO (scio1: /sso)
          {
            path: 'sso',
            element: (
              <LazyWrapper>
                <SsoLoginPage />
              </LazyWrapper>
            ),
          },
          // SSO Login (scio1: /auth/sso-login - this one has /auth/ prefix in scio1)
          {
            path: 'auth/sso-login',
            element: (
              <LazyWrapper>
                <SsoLoginPage />
              </LazyWrapper>
            ),
          },

          // === App routes (protected) — paths match scio1 exactly ===
          {
            path: 'app',
            element: <ProtectedRoute />,
            children: [
              // Main content pages
              {
                path: 'screenManagement',
                element: useNativeScreens ? (
                  <LazyWrapper>
                    <ScreensPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'assetManagement',
                element: useNativeAssets ? (
                  <LazyWrapper>
                    <AssetsPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'playlistManagement',
                element: useNativePlaylists ? (
                  <LazyWrapper>
                    <PlaylistsPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'scheduleManagement',
                element: useNativeSchedules ? (
                  <LazyWrapper>
                    <SchedulesPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'templateManagement',
                element: useNativeTemplates ? (
                  <RestrictedRoute>
                    <LazyWrapper>
                      <TemplatesPage />
                    </LazyWrapper>
                  </RestrictedRoute>
                ) : (
                  <RestrictedRoute>
                    <Scio1Frame />
                  </RestrictedRoute>
                ),
              },
              {
                path: 'engageManagement',
                element: useNativeEngage ? (
                  <LazyWrapper>
                    <EngagePage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'optidev',
                element: (
                  <LazyWrapper>
                    <OptiDevPage />
                  </LazyWrapper>
                ),
              },
              {
                path: 'dev/smallapp-playground',
                element: (
                  <AdminRoute>
                    <LazyWrapper>
                      <ManifestPlaygroundPage />
                    </LazyWrapper>
                  </AdminRoute>
                ),
              },
              {
                path: 'crm-management',
                element: useNativeEngage ? (
                  <LazyWrapper>
                    <EngageHubPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'designManagement',
                element: (
                  <DesignerPermissionGate>
                    {useNativeDesigner ? (
                      <LazyWrapper>
                        <DesignerPage />
                      </LazyWrapper>
                    ) : (
                      <Scio1Frame />
                    )}
                  </DesignerPermissionGate>
                ),
              },
              // /app/designEditor → redirect to /app/designManagement (scio1 public editor is at /designEditor, not /app/)
              {
                path: 'designEditor',
                element: <Navigate to="/app/designManagement" replace />,
              },
              {
                path: 'feeds',
                element: useNativeFeeds ? (
                  <LazyWrapper>
                    <FeedsPageLazy />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },

              // Analytics
              {
                path: 'playbackReport',
                element: (
                  <AdminRoute>
                    <LazyWrapper>
                      <ProofOfPlayPage />
                    </LazyWrapper>
                  </AdminRoute>
                ),
              },
              {
                path: 'ai-analytics',
                element: (
                  <AdminRoute>
                    <LazyWrapper>
                      <AiAnalyticsPage />
                    </LazyWrapper>
                  </AdminRoute>
                ),
              },
              { path: 'proofofplay', element: <Scio1Frame /> },
              {
                path: 'security-audit-logging',
                element: useNativeAuditLog ? (
                  <LazyWrapper>
                    <AuditLogPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },

              // === Settings — SettingsLayout handles native (Outlet) + iframe (Scio1Frame) ===
              {
                path: 's',
                element: <SettingsLayout />,
                children: [
                  // Native pages (render via Outlet when feature-flagged; null = iframe in SettingsLayout)
                  {
                    path: 'personal-profile',
                    element: useNativeProfile ? (
                      <LazyWrapper>
                        <PersonalProfilePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    // Refer & Earn — native-only (no scio1 equivalent); gated + made
                    // a NATIVE_SETTINGS_SLUG by VITE_ENABLE_REFERRAL_ENGINE. The page
                    // self-gates (redirects when the flag is off).
                    path: 'referral',
                    element: (
                      <LazyWrapper>
                        <ReferralPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'company-profile',
                    element: useNativeCompanyProfile ? (
                      <LazyWrapper>
                        <CompanyProfilePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'external-coms',
                    element: useNativeExternalComs ? (
                      <LazyWrapper>
                        <ExternalComsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'preference-settings',
                    element: useNativePreferences ? (
                      <LazyWrapper>
                        <PreferencesPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'apikeys',
                    element: useNativeApiKeys ? (
                      <LazyWrapper>
                        <ApiKeysPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'feeds-admin',
                    element: useNativeFeedsAdmin ? (
                      <LazyWrapper>
                        <FeedsAdminPageLazy />
                      </LazyWrapper>
                    ) : null,
                  },
                  // Iframe-only settings pages
                  {
                    path: 'account-members',
                    element: useNativeAccountMembers ? (
                      <LazyWrapper>
                        <AccountMembersPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'subscription-plan',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <SubscriptionPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'payment',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <PaymentMethodPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'payment-history',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <PaymentHistoryPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'order-device',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <OrderDevicePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'order-device/:alias',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <OrderDevicePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'ordered-device',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <OrderHistoryPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'credentials', element: null },
                  {
                    path: 'operational-schedule',
                    element: useNativeOpsSchedule ? (
                      <LazyWrapper>
                        <OperationalSchedulePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'approval-list', element: null },
                  {
                    path: 'sub-account-billing',
                    element:
                      useNativeBilling && useNativeSubAccountBilling ? (
                        <LazyWrapper>
                          <SubAccountBillingPage />
                        </LazyWrapper>
                      ) : null,
                  },
                  {
                    path: 'team-billing',
                    element:
                      useNativeBilling && useNativeTeams ? (
                        <LazyWrapper>
                          <TeamBillingPage />
                        </LazyWrapper>
                      ) : null,
                  },
                  {
                    path: 'coupons',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <CouponsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'proof-of-play-settings',
                    element: useNativePopSettings ? (
                      <LazyWrapper>
                        <ProofOfPlaySettingsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'branding-settings',
                    element: useNativeBrandingSettings ? (
                      <LazyWrapper>
                        <BrandingSettingsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'monitoring-alert-rules',
                    element: useNativeMonitoringAlertRules ? (
                      <LazyWrapper>
                        <MonitoringAlertRulesPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'saml-settings',
                    element: useNativeSamlSettings ? (
                      <LazyWrapper>
                        <SamlSettingsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'integrations',
                    element: useNativeIntegrations ? (
                      <LazyWrapper>
                        <IntegrationsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'revue',
                    element: (
                      <LazyWrapper>
                        <RevuePage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'share-links-admin',
                    element: useNativeShareLinksAdmin ? (
                      <LazyWrapper>
                        <ShareLinksAdminPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'datasources-admin',
                    element: useNativeDataSources ? (
                      <LazyWrapper>
                        <DataSourcesPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'social', element: null },
                  {
                    path: 'functional-locations',
                    element: useNativeFunctionalLocations ? (
                      <LazyWrapper>
                        <FunctionalLocationsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'confirm-linked-account',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <ConfirmLinkedAccountPage />
                      </LazyWrapper>
                    ) : null,
                  },

                  // scio1-only slugs — redirected by SettingsLayout via SETTINGS_REDIRECTS map
                  // (routes still needed so React Router matches them instead of falling through)
                  { path: 'android-devices', element: null },
                  { path: 'free-devices', element: null },
                  { path: 'payment-thankyou', element: null },
                  {
                    path: 'payment-intent',
                    element: useNativeBilling ? (
                      <LazyWrapper>
                        <PaymentIntentPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'subscription-thankyou', element: null },
                  { path: 'subscription-init-thankyou', element: null },
                  { path: 'proof-of-play-redirect', element: null },
                  { path: 'request-apis', element: null },
                  // Default settings page
                  { index: true, element: <Navigate to="personal-profile" replace /> },
                ],
              },

              // === OptiEdge MDM — Devices + Fleets (Phase 1) + Connect (Phase A) ===
              // Gated by `company.feature.mdm` on the optisigns variant;
              // always available on the optiedge variant (detected via hostname).
              // `ConnectLayout` wraps every /app/mdm/** route with the local
              // `MdmSidebar` chrome + mounts the shared `ActivateSignageDialog`.
              {
                path: 'mdm',
                element: (
                  <MdmFeatureRoute>
                    <RestrictedRoute>
                      <LazyWrapper>
                        <ConnectLayout />
                      </LazyWrapper>
                    </RestrictedRoute>
                  </MdmFeatureRoute>
                ),
                children: [
                  // Phase 1 — Devices + Fleets
                  {
                    index: true,
                    element: (
                      <LazyWrapper>
                        <DevicesPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'fleets',
                    element: (
                      <LazyWrapper>
                        <FleetsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'fleets/:fleetId',
                    element: (
                      <LazyWrapper>
                        <FleetDetailPage />
                      </LazyWrapper>
                    ),
                  },
                  // Phase A — Connect section.
                  // MDM frontend redesign D1 (2026-05-27): `/app/mdm/connect`
                  // now lands on the Connect Hub overview instead of
                  // redirecting to the first vendor. Per-vendor pages stay
                  // at the same paths.
                  {
                    path: 'connect',
                    element: (
                      <LazyWrapper>
                        <ConnectHubPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'connect/zoom-rooms',
                    element: (
                      <LazyWrapper>
                        <ZoomRoomsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'connect/teams-rooms',
                    element: (
                      <LazyWrapper>
                        <TeamsRoomsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'connect/webex-rooms',
                    element: (
                      <LazyWrapper>
                        <WebexRoomsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'connect/google-meet',
                    element: (
                      <LazyWrapper>
                        <GoogleMeetPage />
                      </LazyWrapper>
                    ),
                  },
                  // 802.1X / Phase A.4-bis — MDM-scoped library pages.
                  // See docs/specs/19-optiedge-mdm/802.1x/ux-provision-templates-network-design.md
                  {
                    path: 'templates',
                    element: (
                      <LazyWrapper>
                        <ProvisioningTemplatesPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: 'wifi-profiles',
                    element: (
                      <LazyWrapper>
                        <WifiProfilesPage />
                      </LazyWrapper>
                    ),
                  },
                ],
              },

              ...(devMod?.devAppRoutes ?? []),

              // Catch-all for unmapped app routes
              { path: '*', element: <Scio1Frame /> },
            ],
          },

          // Catch-all redirect
          { path: '*', element: <Navigate to="/app/screenManagement" replace /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
